/**
 * Coaches tab — WC head coaches grouped by country.
 *
 * Coaches are WC-only. When EPL is selected the tab renders an explicit
 * empty state directing the admin to switch leagues, rather than a generic
 * "no rows" message. Backed by `GET /v1/admin/coaches`.
 */

import { useEffect, useMemo, useState } from 'react';

import { Input } from '@/components/ui/input';

import { type AdminCoachRow, type LeagueName, useAdminCoaches } from './api';
import { DASH, DataTable, DataTableShell } from './shared';

export function CoachesTab({ league }: { league: LeagueName }) {
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => window.clearTimeout(t);
  }, [q]);

  const { data, isLoading, error } = useAdminCoaches({
    q: debouncedQ || undefined,
    league,
  });
  const rows = data?.data ?? [];

  // Group counts by country for a "X countries" label. The list itself is
  // already sorted by (country_code, full_name) by the backend.
  const countryCount = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) set.add(r.country_code);
    return set.size;
  }, [rows]);

  if (league !== 'WC') {
    return (
      <div className="rounded-md border border-dashed bg-muted/20 px-4 py-12 text-center text-sm text-muted-foreground">
        Coaches are WC-only — switch to World Cup to view.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <div className="min-w-[280px] flex-1">
          <Input
            placeholder="Search coach name…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <span className="text-xs text-muted-foreground">
          {rows.length} coaches · {countryCount} countries
        </span>
      </div>

      <DataTableShell loading={isLoading} error={error} empty={rows.length === 0}>
        <DataTable<AdminCoachRow>
          rows={rows}
          rowKey={(r) => r.id}
          columns={[
            {
              key: 'country_code',
              label: 'Country',
              render: (r) => (
                <span className="font-mono text-xs uppercase">{r.country_code}</span>
              ),
            },
            {
              key: 'country_name',
              label: 'Country name',
              render: (r) => r.country_name ?? DASH,
            },
            {
              key: 'name',
              label: 'Coach',
              render: (r) => <span className="font-medium text-foreground">{r.name}</span>,
            },
            {
              key: 'is_sacked',
              label: 'Status',
              render: (r) =>
                r.is_sacked ? (
                  <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-xs text-destructive">
                    Sacked
                  </span>
                ) : (
                  <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs text-emerald-700 dark:text-emerald-300">
                    Active
                  </span>
                ),
            },
            {
              key: 'external_id',
              label: 'External ID',
              render: (r) => <code className="text-xs">{r.external_id ?? DASH}</code>,
            },
          ]}
        />
      </DataTableShell>
    </div>
  );
}
